<template>
  <accordion-section
    :open="section === 7"
    :valid="periods.length > 0"
    @selected="$emit('openSection', 7)"
  >
    <template #heading>
      Opening Times
    </template>

    <div class="opening_times_content">
      <div class="times">
        <div
          v-for="(day, index) in daysOfWeek"
          :key="index"
          class="time_item"
        >
          <div class="day_of_week">
            {{ day.name }}
          </div>
          <custom-toggle
            v-model="day.open"
            :disabled="!canEditLocation"
            :label="day.open ? 'Open' : 'Closed'"
          />
          <div class="periods">
            <div
              v-for="(period, periodIndex) in day.periods"
              :key="periodIndex"
              class="hours"
            >
              <custom-select
                v-model="period.open"
                :groups="true"
                :options="selectableTimes"
                :disabled="!canEditLocation"
                placeholder="Select"
              />
              <span
                v-if="period.open !== '24h'"
                class="to"
              >to</span>
              <custom-select
                v-if="period.open !== '24h'"
                v-model="period.close"
                :groups="true"
                :options="selectableTimes"
                :disabled="!canEditLocation"
                placeholder="Select"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="canEditLocation"
        class="helper_btns"
      >
        <primary-button
          :show-icon="true"
          class="helper_btn"
          size="md"
          @click="applyTo()"
        >
          <template #icon>
            <svg-vue
              class="arrow_down"
              icon="icons/arrow_down_white"
            />
          </template>
          All
        </primary-button>

        <secondary-button
          :show-icon="true"
          class="helper_btn"
          size="md"
          @click="applyTo('weekdays')"
        >
          <template #icon>
            <svg-vue
              class="arrow_down"
              icon="icons/arrow_down_white"
            />
          </template>
          Weekdays
        </secondary-button>
      </div>
    </div>
  </accordion-section>
</template>

<script>
import _ from 'lodash';
import AccordionSection from "../items/AccordionSection";
import {mapActions, mapGetters, mapMutations} from "vuex";
import CustomSelect from "../../../../../views/reports/components/global/forms/CustomSelect";
import PrimaryButton from "../../../global/buttons/PrimaryButton";
import SecondaryButton from "../../../global/buttons/SecondaryButton";
import moment from 'moment';
import CustomToggle from "../../../../../views/reports/components/global/forms/CustomToggle";

export default {
  components: {
    CustomToggle,
    SecondaryButton,
    PrimaryButton,
    CustomSelect,
    AccordionSection
  },
  props: {
    section: {
      type: Number,
      required: true,
    },
  },
  emits: ['openSection'],
  data() {
    return {
      daysOfWeek: {
        MONDAY: {
          name: 'Monday',
          open: false,
        },
        TUESDAY: {
          name: 'Tuesday',
          open: false,
        },
        WEDNESDAY: {
          name: 'Wednesday',
          open: false,
        },
        THURSDAY: {
          name: 'Thursday',
          open: false,
        },
        FRIDAY: {
          name: 'Friday',
          open: false,
        },
        SATURDAY: {
          name: 'Saturday',
          open: false,
        },
        SUNDAY: {
          name: 'Sunday',
          open: false,
        },
      },
      weekdays: [
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY'
      ],
      regularHours: null,
    }
  },
  computed: {
    ...mapGetters('user', [
        'canEditLocation',
    ]),
    ...mapGetters('businessProfile', [
      'periods',
    ]),
    openDays() {
      return _.mapValues(this.daysOfWeek, function (day) {
        return day.open
      });
    },
    selectableTimes() {
      let options = [];
      options.push({'options': ['24h']})

      let selectableTimes = [];
      let time = moment().startOf('day');

      for (let i = 0; i < 48; i++) {
        selectableTimes.push(time.format('hh:mm A'));
        time.add(30, 'm');
      }

      options.push({'options': selectableTimes});

      return options;
    },
  },
  mounted() {
    this.formatGbpPeriodsToTimes(this.periods);
    this.$watch(
        'daysOfWeek',
        () => {
          this.updatePeriods(this.convertTimesToGbpPeriods(this.daysOfWeek));
        },
        {deep: true}
    );
    this.$watch(
        'openDays',
        () => {
          _.forEach(this.openDays, (open, day) => {
            if (!open) {
              delete this.daysOfWeek[day].periods
              return;
            }
            if (!this.daysOfWeek[day].periods) {
              this.daysOfWeek[day].periods = [
                {"open": "", "close": ""}
              ];
            }
          });
        }
    )
  },
  methods: {
    ...mapMutations('editListing', [
      'setSelectedSection',
    ]),
    ...mapActions('businessProfile', [
      'updatePeriods',
    ]),
    formatGbpPeriodsToTimes(periods) {
      _.each(periods, (period) => {
        this.daysOfWeek[period.openDay].open = true;
        this.daysOfWeek[period.openDay].periods = this.daysOfWeek[period.openDay].periods || [];
        this.daysOfWeek[period.openDay].periods.push(this.convertGbpPeriodToTimes(period));
      });
    },
    convertGbpPeriodToTimes(period) {
      return {
        'open': moment(period.openTime).format('hh:mm A'),
        'close': moment(period.closeTime).format('hh:mm A')
      };
    },
    convertTimesToGbpPeriods(days) {
      let gbpPeriods = [];
      _.forEach(days, (day, dayEnum) => {
        _.forEach(day.periods, (period) => {
          const open = moment(period.open, 'hh:mm A', true);
          const close = moment(period.close, 'hh:mm A', true);

          let openHour = period.open === '24h' ? null : open.hour();
          let openMinute = period.open === '24h' ? null : open.minute();
          let closeHour = period.open === '24h' ? null : close.hour();
          let closeMinute = period.open === '24h' ? null : close.minute();

          let formattedPeriod = {
            "closeDay": dayEnum,
            "openDay": dayEnum,
            "openTime": {
              "hours": openHour,
              "minutes": openMinute,
              "nanos": null,
              "seconds": null
            },
            "closeTime": {
              "hours": closeHour,
              "minutes": closeMinute,
              "nanos": null,
              "seconds": null
            }
          };

          gbpPeriods.push(formattedPeriod);
        });
      });
      return gbpPeriods;
    },
    applyTo(period = 'all') {
      const dayToCopy = this.daysOfWeek.MONDAY;
      let daysToLoop = Object.keys(this.daysOfWeek);
      console.log(daysToLoop);
      if (period === 'weekdays') {
        daysToLoop = this.weekdays
      }
      _.each(daysToLoop, day => {
        this.daysOfWeek[day].open = dayToCopy.open;
        this.daysOfWeek[day].periods = dayToCopy.periods;
      });
    },
  },
}
</script>

<style lang="scss" scoped>
.opening_times_content {
  display: flex;

  > .times {
    width: 80%;

    > .time_item {
      height: 34px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:not(:last-of-type) {
        margin-bottom: .5rem;
      }

      > .day_of_week {
        width: 19%;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.72;
        letter-spacing: -0.00715em;
        color: #4E4E54;
      }

      :deep(.toggle_wrap) {
        width: 21%;
      }

      > .periods {
        display: flex;
        width: 60%;
        flex-direction: column;

        > .hours {
          display: flex;
          align-items: center;

          :deep(.custom_select) {
            --ms-py: 6px;
          }

          :deep(.multiselect-single-label) {
            padding-right: var(--ms-px, .875rem);
          }

          :deep(.custom_input) {
            width: 44%;
            margin-bottom: 0;
            margin-left: 10px;

            &:not(:nth-of-type(2)) {
              margin-right: 10px;
            }
          }

          > .to {
            text-align: center;
            font-size: 0.75rem;
            line-height: 1.334;
            letter-spacing: 0.00834em;
            color: #4E4E54;
          }
        }
      }
    }
  }

  > .helper_btns {
    display: flex;
    align-items: flex-start;
    flex: 1;
    flex-direction: column;

    .helper_btn {
      margin-bottom: .5rem;
      margin-left: .5rem;

      .arrow_down {
        height: 1rem;
        width: 1rem;
      }
    }
  }
}
</style>
